import Urn from '@adc/structures/urn'
import type { Maybe, Listing, AuctionTransaction } from '@adc/sdk/types'
import type { ContextOutput, ContextSourceType } from './index'

/**
 * Reads the BRID cookie value from the browser.
 */
const getBridCookie = (): string => {
  const match = document.cookie.match(/(?:^|;\s*)BRID=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : ''
}

/**
 * Proper-cases a resource type string, e.g. 'listing' -> 'Listing', 'transaction' -> 'Transaction'
 */
export const properCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

/**
 * Extracts the ID portion from any URN format (e.g., "adc:listing:12345" -> "12345",
 * "adc:transaction:0qw0r475yx957" -> "0qw0r475yx957")
 */
export function extractIdFromUrn(urn: string): string | null {
  return Urn.safelyParse(urn)?.identifier ?? null
}

/**
 * Extracts listing ID from URN format (e.g., "adc:listing:12345" -> "12345")
 */
export function extractListingIdFromUrn(urn: string): string | null {
  const match = urn.match(/^adc:listing:(\d+)$/)
  return match ? match[1] : null
}

/**
 * Extracts transaction ID from URN format (e.g., "adc:transaction:0qw0r475yx957" -> "0qw0r475yx957")
 */
export function extractTransactionIdFromUrn(urn: string): string | null {
  const match = urn.match(/^adc:transaction:(.+)$/)
  return match ? match[1] : null
}

/**
 * Detects the source type from a URN string
 */
export function detectSourceTypeFromUrn(urn: string): ContextSourceType {
  if (urn.startsWith('adc:listing:')) return 'listing'
  if (urn.startsWith('adc:transaction:')) return 'transaction'
  // Fallback: try to extract type from urn pattern adc:{type}:{id}
  const parsed = Urn.safelyParse(urn)
  if (parsed) return parsed.resourceType as ContextSourceType
  return 'listing'
}

/**
 * Formats the selected resource into the context object structure
 * expected by the backend API
 */
export const formatContext = (
  selectedListing: Maybe<Partial<Listing>>,
  sourceType: ContextSourceType,
  selectedUserEmail?: string,
  runAsMode: 'authenticated' | 'unauthenticated' = 'authenticated',
  selectedTransaction?: Maybe<Partial<AuctionTransaction>>,
  rawId?: string
): ContextOutput | null => {
  // Build run_as value
  let run_as: string | undefined
  if (runAsMode === 'unauthenticated') {
    const brid = getBridCookie()
    run_as = new Urn('anonymous_session', brid).toString()
  } else if (selectedUserEmail) {
    run_as = new Urn('email', selectedUserEmail).toString()
  }

  if (sourceType === 'listing') {
    if (!selectedListing?.listing_id) {
      if (run_as) {
        return { sources: [], sourceType, run_as }
      }
      return null
    }

    const context: ContextOutput = {
      sources: [new Urn('listing', selectedListing.listing_id).toString()],
      sourceType: 'listing',
    }

    // Extract first line of address as contextTitle
    const addressLine = selectedListing.formatted_address?.[0]
    if (addressLine) {
      context.contextTitle = addressLine
    }

    if (run_as) {
      context.run_as = run_as
    }

    return context
  }

  if (sourceType === 'transaction') {
    if (!selectedTransaction?.transaction_urn) {
      if (run_as) {
        return { sources: [], sourceType, run_as }
      }
      return null
    }

    const sources = [selectedTransaction.transaction_urn]
    // Include associated listing URN so workflow can be resolved
    if (selectedTransaction.listing?.listing_id) {
      sources.push(
        new Urn('listing', selectedTransaction.listing.listing_id).toString()
      )
    }

    const context: ContextOutput = {
      sources,
      sourceType: 'transaction',
    }

    // Context title: listing address if available, else property address, else transaction ID
    const listingAddr = selectedTransaction.listing?.formatted_address?.[0]
    let addressLine: string | null = listingAddr || null

    if (!addressLine && selectedTransaction.property?.summary?.address) {
      const addr = selectedTransaction.property.summary.address
      addressLine =
        [addr.street_number, addr.street_name].filter(Boolean).join(' ') || null
    }
    if (addressLine) {
      context.contextTitle = addressLine
    } else {
      const txId = extractIdFromUrn(selectedTransaction.transaction_urn)
      if (txId) {
        context.contextTitle = txId
      }
    }

    if (run_as) {
      context.run_as = run_as
    }

    return context
  }

  // Generic/unknown source type — use rawId
  if (rawId) {
    const context: ContextOutput = {
      sources: [`adc:${sourceType}:${rawId}`],
      sourceType,
      contextTitle: rawId,
    }
    if (run_as) {
      context.run_as = run_as
    }
    return context
  }

  if (run_as) {
    return { sources: [], sourceType, run_as }
  }

  return null
}

/**
 * Validates that a context object is complete and valid
 */
export const isValidContext = (context: ContextOutput | null): boolean => {
  return !!(context && context.sources.length > 0)
}

/**
 * Parses context from evaluation group format to ContextOutput format
 */
export function parseContext(context?: {
  sources: Array<string>
}): ContextOutput | null {
  if (!context?.sources || context.sources.length === 0) {
    return null
  }

  const firstSource = context.sources[0]
  const sourceType = detectSourceTypeFromUrn(firstSource)

  return {
    sources: context.sources,
    sourceType,
  }
}
